import { Button, Card, Col, DatePicker, Empty, Form, Row, Space } from "antd";
import VoucherChart from "./VoucherChart";
import HomePieChart from "./HomePieChart";
import { useEffect, useState } from "react";
import axiosClient from "../../api/axiosClient";
import ButtonSwitch from "../../components/ButtonSwitch";

import { CalendarIcon } from "../../assets";

import { useForm } from "antd/es/form/Form";
import Select from "../../components/Select";
import handleApiError from "../../utils/handleError";
import { Loading } from "../../components";
import dayjs from "dayjs";

type Stats = {
  exam_vise_collection: Record<string, unknown>;
  total_ammount: number;
  total_atcs: number;
  total_exams: number;
  total_users: number;
  total_vouchers: number;
  used_vouchers: number;
};

const buttons = ["ATC Admin", "Public Users"];

const formLayout = { xs: 24, sm: 12, md: 12, lg: 6 };

const todayRange = {
  from: dayjs().startOf("day"),
  to: dayjs().endOf("day"),
};

const todayFilter = `/filter?from=${todayRange.from.unix()}&to=${todayRange.to.unix()}`;

export default function Home() {
  const [form] = useForm();
  const [stats, setStats] = useState<Stats>();
  const [data, setData] = useState<any>([]);
  const [atcs, setAtcs] = useState<any>([]);
  const [activeButton, setActiveButton] = useState<any>("ATC Admin");
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [range, setRange] = useState<any>(todayRange);
  const [filter, setFilter] = useState<any>(todayFilter);

  const isPublic = activeButton === "Public Users";

  useEffect(() => {
    const endpoint = `/homepage/stats/${isPublic ? "public" : "atc"}${filter}`;
    setFormLoading(true);
    axiosClient
      .get(endpoint)
      .then((res) => setStats(res.data))
      .catch((err) => handleApiError(err))
      .finally(() => {
        setFormLoading(false);
      });
  }, [isPublic, filter, form]);

  useEffect(() => {
    async function getData() {
      try {
        let res: any;
        res = await axiosClient.get("/atcs");
        setAtcs(res.data);
      } catch (error) {
        handleApiError(error);
      } finally {
        setLoading(false);
      }
    }
    getData();
  }, []);

  const checkAndSubmit = async () => {
    const values = await form.getFieldsValue();
    let isAllField = true;
    Object.keys(values).forEach((key) => {
      if (values[key] === undefined) {
        isAllField = false;
      }
    });
    if (isAllField) {
      form.submit();
    } else if (values.from && values.to) {
      return form.submit();
    }
  };

  const onFinish = async (values: any) => {
    const range: any = { from: values.from, to: values.to.endOf("day") };
    const idKey = isPublic ? "exam_id" : "atc_id";
    const idValue = isPublic ? values.exam_id : values.atc_id;
    const fromTimestamp = range.from.unix();
    const toTimestamp = range.to.unix();
    let filter = `/filter?from=${fromTimestamp}&to=${toTimestamp}`;
    if (idValue) filter += `&${idKey}=${idValue}`;
    setRange(range);
    setFilter(filter);
  };

  useEffect(() => {
    if (stats) {
      const data = Object.keys(stats.exam_vise_collection).map((key) => ({
        name: key,
        value: stats.exam_vise_collection[key],
      }));
      setData(data);
    }
  }, [stats]);

  function clearFilter() {
    setFilter(todayFilter);
    setRange(todayRange);
  }

  const handleSwitch = (button: string) => {
    setActiveButton(button);
    clearFilter();
    form.resetFields();
  };

  if (loading) return <Loading />;
  if (!stats) return null;
  return (
    <div className="home_container">
      <div style={{ maxWidth: "1200px" }}>
        <Row gutter={[20, 20]}>
          <Col flex={1}>
            <Card bordered={false}>
              <h1>{stats.total_users}</h1>
              <h5>Total Users</h5>
            </Card>
          </Col>
          <Col flex={1}>
            <Card bordered={false}>
              <h1>{stats.total_exams}</h1>
              <h5>Total Exams</h5>
            </Card>
          </Col>
          <Col flex={1}>
            <Card bordered={false}>
              <h1>{stats.total_atcs}</h1>
              <h5>Total ATC </h5>
            </Card>
          </Col>
          <Col flex={1} className="voucher_chart">
            <VoucherChart
              data={[
                { name: "Generated Vouchers", value: stats.total_vouchers },
                { name: "Used Vouchers", value: stats.used_vouchers },
              ]}
            />
          </Col>
        </Row>
      </div>
      <Space style={{ margin: "30px 0" }}>
        <ButtonSwitch buttons={buttons} onSwitch={handleSwitch} />
      </Space>

      <Form
        layout="vertical"
        form={form}
        onValuesChange={checkAndSubmit}
        onFinish={onFinish}
        initialValues={{ from: range.from, to: range.to }}
        className="home_form"
      >
        <Row gutter={[20, 20]}>
          <Col {...formLayout}>
            <Form.Item label="From" name="from">
              <DatePicker
                placeholder="DD/MM/YYYY"
                className="full-width"
                format="DD/MM/YYYY"
                suffixIcon={<CalendarIcon />}
              />
            </Form.Item>
          </Col>
          <Col {...formLayout}>
            <Form.Item label="To" name="to">
              <DatePicker
                placeholder="DD/MM/YYYY"
                className="full-width"
                format="DD/MM/YYYY"
                suffixIcon={<CalendarIcon />}
              />
            </Form.Item>
          </Col>
          {!isPublic ? (
            <Col {...formLayout}>
              <Select
                placeholder={"Select ATC"}
                options={atcs}
                formItemProps={{
                  name: "atc_id",
                  label: " ",
                }}
              />
            </Col>
          ) : null}
        </Row>
      </Form>

      <Button type="primary" style={{ backgroundColor: "#533031" }}>
        Total Amount Received: ${stats.total_ammount}
      </Button>

      {formLoading ? <Loading /> : null}

      <h5>ATC Exam Collection</h5>
      <Row justify="center">
        {data.length > 0 ? (
          <div className="home_pie-chart-2">
            <HomePieChart data={data} />
          </div>
        ) : (
          <Empty style={{ marginTop: 20 }} />
        )}
      </Row>
    </div>
  );
}
